import type { CanvasContext } from '@studio/design-system/canvas';
import { Canvas } from '@studio/design-system/canvas';
import type { Coordinate } from '@studio/js-utils/geometry';
import { composeHandlers } from '@studio/react-utils/event';
import { useComposedRefs, useDocumentActive, useLatestFn, χref } from '@studio/react-utils/hooks';
import type { PointerEvent } from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import { twMerge } from 'cn';
import { isPrimaryPointer } from '../utils.js';
import type { Props } from './types.js';
import type { T_Maybe } from '@tryiris-ai/js-types';

let lastPosition = { x: 0, y: 0 };

/** `<Cursor>` component. */
export const Cursor = forwardRef<CanvasContext, Props>(
  (
    { className, initialPosition, onDrawCursor, onPointerLeave, onPointerMove, ...props },
    forwardedRef
  ) => {
    const positionRef = useRef(initialPosition);
    const canvasRef = useRef<CanvasContext>(null);
    const isDocumentActive = useDocumentActive();

    const setPosition = useLatestFn((nextPosition: Coordinate | undefined): void => {
      const currentPosition = χref(positionRef);

      const changed =
        // Both undefined?
        currentPosition === undefined && nextPosition === undefined
          ? false
          : // Only one is undefined?
            currentPosition === undefined || nextPosition === undefined
            ? true
            : // Dig in
              currentPosition.x !== nextPosition.x || currentPosition.y !== nextPosition.y;

      if (!changed) {
        return;
      }

      positionRef.current = nextPosition;

      χref(canvasRef)?.draw();
    });

    // Watch for document activity changes.
    useEffect(() => {
      if (!isDocumentActive) {
        setPosition(undefined);
      }
    }, [isDocumentActive]);

    // Handle pointer leave.
    const handlePointerLeave = useLatestFn((event: PointerEvent<HTMLCanvasElement>) => {
      if (!isPrimaryPointer(event)) {
        return;
      }

      setPosition(undefined);
    });

    // Handle pointer movement.
    const handlePointerMove = useLatestFn((event: PointerEvent<HTMLCanvasElement>) => {
      if (!isPrimaryPointer(event)) {
        return;
      }

      const rect = χref(canvasRef)?.canvas?.getBoundingClientRect();

      const position = rect
        ? {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top
          }
        : undefined;

      // Keep track of the pointer position.
      setPosition(position);

      if (position !== undefined) {
        lastPosition = position;
      }
    });

    const composedPointerLeave = useLatestFn(composeHandlers(onPointerLeave, handlePointerLeave));
    const composedPointerMove = useLatestFn(composeHandlers(onPointerMove, handlePointerMove));

    return (
      <Canvas
        className={twMerge('absolute inset-0', className)}
        onDraw={(context): void => {
          onDrawCursor(context, χref(positionRef));
        }}
        onPointerLeave={composedPointerLeave}
        onPointerMove={composedPointerMove}
        ref={useComposedRefs(canvasRef, forwardedRef)}
        {...props}
      />
    );
  }
);

/** Get the last known position of the cursor. */
export const getLastKnownPosition = (): T_Maybe<{ x: number; y: number }> =>
  lastPosition.x === 0 && lastPosition.y === 0 ? undefined : lastPosition;
